import { useFormContext } from 'react-hook-form';

import useFleetAdminPermissions from 'utils/User/useFleetAdminPermissions';

interface AccountLookup {
  id: string;
  sentinelTeam?: {
    oemSegment?: { id?: string | null } | null;
  } | null;
}

interface UseCreateDepotCapabilityArgs {
  /**
   * Provide directly when the modal is opened with a fixed account in
   * scope (e.g. from the Account detail page). When omitted, the segment
   * is derived from the in-modal Account dropdown's selection, using
   * the `accounts` argument as the lookup source.
   */
  oemSegmentId?: string;
  /**
   * The accounts collection already loaded by the modal for its Account
   * dropdown. The hook does not run its own query — the caller owns the
   * single source of truth for accounts data.
   */
  accounts?: ReadonlyArray<AccountLookup>;
}

interface UseCreateDepotCapabilityResult {
  selectedSegmentId?: string;
  /**
   * `null` when no segment can be resolved yet (e.g. the user hasn't
   * picked an account, or the caller didn't supply one and no accounts
   * are available to look up).
   */
  capability: FleetAdminCapability | null;
}

/**
 * Resolve the Fleet Admin "canManageDepots" capability for whatever
 * OEM segment the CreateDepotModal currently points at. Pure compute
 * over its inputs — no Apollo subscriptions of its own.
 */
export default function useCreateDepotCapability({
  oemSegmentId,
  accounts,
}: UseCreateDepotCapabilityArgs): UseCreateDepotCapabilityResult {
  const { watch } = useFormContext();
  const fleetAdminPermissions = useFleetAdminPermissions();

  const formAccountId = watch('accountId');

  const derivedSegmentId =
    oemSegmentId ||
    accounts?.find((account) => account.id === formAccountId)?.sentinelTeam
      ?.oemSegment?.id ||
    undefined;

  if (!derivedSegmentId) {
    return { selectedSegmentId: undefined, capability: null };
  }

  return {
    selectedSegmentId: derivedSegmentId,
    capability: fleetAdminPermissions.capability(
      derivedSegmentId,
      'canManageDepots',
    ),
  };
}
